import { useEffect, useState } from 'react';
import { listAdminProducts, listAllProductFiles, type AdminProductRelease } from '@/services/products';
import type { Product } from '@/domain/types';

export default function AdminDownloads() {
  const [releases, setReleases] = useState<AdminProductRelease[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    Promise.all([listAllProductFiles(), listAdminProducts()])
      .then(([files, prods]) => {
        if (alive) { setReleases(files); setProducts(prods); }
      })
      .catch((err) => { if (alive) setError(err?.message ?? 'Unable to load releases'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = releases.filter((r) => {
    if (selectedProduct && r.productId !== selectedProduct) return false;
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    return true;
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-surface-900">Releases</h1>
        <p className="text-sm text-surface-500 mt-1">All uploaded product files and versions.</p>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="bg-white rounded-xl border border-surface-200 p-4 flex flex-col sm:flex-row gap-4 sm:items-end">
        <div>
          <label className="block text-xs font-medium text-surface-600 mb-1.5">Product</label>
          <select value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)} className="px-3 py-2 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500">
            <option value="">All Products</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-surface-600 mb-1.5">Status</label>
          <div className="flex items-center gap-2">
            {['all', 'active', 'archived'].map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-2 rounded-lg text-xs font-medium capitalize transition-colors ${statusFilter === s ? 'bg-brand-600 text-white' : 'border border-surface-200 text-surface-600 hover:bg-surface-50'}`}>{s}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Product</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">File</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Version</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Size</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Access</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Status</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-5 py-8 text-center text-surface-500">Loading releases...</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-surface-500">No releases found.</td></tr>}
              {filtered.map((rel) => (
                <tr key={rel.id} className="border-b border-surface-50 hover:bg-surface-50/50">
                  <td className="px-5 py-3 font-semibold text-surface-900">{rel.productName}</td>
                  <td className="px-5 py-3 text-surface-600 max-w-xs truncate">{rel.downloadFileName}</td>
                  <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-md bg-brand-50 text-brand-700 text-xs font-semibold">v{rel.version}</span></td>
                  <td className="px-5 py-3 text-surface-500 text-xs">{rel.fileSize ? `${Math.round(rel.fileSize / 1024)} KB` : '—'}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${rel.accessLevel === 'free' ? 'bg-green-50 text-green-700' : 'bg-sky-50 text-sky-700'}`}>{rel.accessLevel}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${rel.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-surface-100 text-surface-500'}`}>{rel.status}</span>
                  </td>
                  <td className="px-5 py-3 text-surface-500 text-xs">{new Date(rel.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
